import React from 'react';
import { Box } from '@mui/material';

/**
 * A 1–5 rating as stars (Michael, 10/23: "stars instead of numbers"):
 * filled ★ for the rating, outlined ☆ for the rest — a shape difference,
 * not only a colour one — read out as "Assistant: 4 of 5 stars". 0 is
 * unrated: blank, keeping the stars' space (Michael, 10/23), read out as
 * "Assistant: unrated".
 */
export function RatingStars({ value, label }: { value: number; label?: string }) {
  const filled = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <Box
      component="span"
      role="img"
      aria-label={`${label ? `${label}: ` : ''}${filled === 0 ? 'unrated' : `${filled} of 5 stars`}`}
      data-testid="rating-stars"
      data-rating={filled}
      sx={{ whiteSpace: 'nowrap', letterSpacing: '1px' }}
    >
      <Box component="span" aria-hidden="true" sx={{ color: 'warning.dark' }}>
        {'★'.repeat(filled)}
      </Box>
      <Box
        component="span"
        aria-hidden="true"
        data-testid="rating-stars-empty"
        sx={{ color: 'grey.500', visibility: filled === 0 ? 'hidden' : undefined }}
      >
        {'☆'.repeat(5 - filled)}
      </Box>
    </Box>
  );
}

/**
 * Both of a chat's ratings, one above the other and each named (Michael,
 * 10/23: there can be two — the assistant's, and support's once a person
 * joined), labels in one column so the stars line up.
 */
export function RatingsStack({ assistant, support }: { assistant: number; support?: number }) {
  const row = (label: string, value: number) => (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {/* The stars' own label already names it: read once, not twice (attack/defend 10/26). */}
      <Box component="span" aria-hidden="true" sx={{ minWidth: 58, fontSize: 12, color: 'text.secondary' }}>
        {label}
      </Box>
      <RatingStars value={value} label={label} />
    </Box>
  );
  return (
    <Box component="span" data-testid="ratings-stack" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: 12, lineHeight: 1.5 }}>
      {row('Assistant', assistant)}
      {support !== undefined && row('Support', support)}
    </Box>
  );
}
